'use client'

import { useState } from 'react'
import { useSearchParams } from 'next/navigation'
import { motion, useReducedMotion } from 'framer-motion'

import { ApplyChat } from './ApplyChat'
import { ApplyFormStepper } from './ApplyFormStepper'

// Chat | Form — the two tab definitions driving the animated pill below.
const TAB_DEFS = [
  { key: 'chat', label: 'Chat' },
  { key: 'form', label: 'Form' },
]

// Chat|Form switcher — Chat selected by default (spec §1). Both tabs share
// the same application doc: identity is threaded purely through the
// `?invite=` URL param, which either tab can mint/update, so switching
// never loses data.
//
// BOTH panels stay mounted; switching toggles visibility (display:none via
// Tailwind's `hidden`) instead of unmounting. Unmounting ApplyChat would
// wipe the visible conversation — and kill a live voice session — every
// time the founder peeked at the Form tab. display:none also removes the
// hidden panel from the focus order and the accessibility tree entirely,
// so the inactive tab can never capture focus or scroll.
export function ApplyTabs(props) {
  const [tab, setTab] = useState('chat')
  const params = useSearchParams()
  const inviteToken = params.get('invite') || null
  const shouldReduceMotion = useReducedMotion()

  // Server-fresh { remaining, complete, … } snapshot, refreshed on every
  // tab switch. The panels are seeded once from SSR props, so without this
  // a dimension covered via chat mid-session would show no ✓ chip on the
  // Form tab (and vice versa), and the submit gate would judge stale
  // coverage. Best-effort: a failed fetch just leaves the seeded state.
  const [syncedState, setSyncedState] = useState(null)

  function selectTab(next) {
    if (next === tab) return
    setTab(next)
    if (!inviteToken) return
    fetch(`/api/z2h/application-state?invite=${encodeURIComponent(inviteToken)}`)
      .then((res) => res.json())
      .then((data) => {
        // `at` forces the effect in each panel to re-run even when the
        // payload happens to be deep-equal to the previous refresh.
        if (data?.ok) setSyncedState({ ...data, at: Date.now() })
      })
      .catch(() => {})
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {/* Animated pill switcher — the gold pill glides between tabs via a
          shared framer-motion layoutId (reactbits PillNav idiom, hand-built:
          the registry's PillNav ships a react-router-dom dependency that
          doesn't fit the Next.js App Router, so this reproduces the same
          shared-layout-transition visual language with the project's
          existing framer-motion dependency instead). Buttons keep a 44px
          min touch target even though the pill reads visually slimmer. */}
      <div
        role="tablist"
        aria-label="Application method"
        className="mt-6 flex flex-wrap justify-center gap-1.5 [@media(max-height:640px)]:mt-3"
      >
        {TAB_DEFS.map(({ key, label }) => {
          const active = tab === key
          return (
            <button
              key={key}
              type="button"
              role="tab"
              aria-selected={active}
              onClick={() => selectTab(key)}
              className={`relative flex min-h-11 min-w-[4.5rem] items-center justify-center rounded-full px-4 py-1.5 text-xs font-semibold tracking-wide uppercase transition-colors ${
                active
                  ? 'text-neutral-950'
                  : 'border border-white/15 text-neutral-400 hover:border-white/30 hover:text-neutral-200'
              }`}
            >
              {active && (
                <motion.span
                  layoutId="apply-tab-pill"
                  className="absolute inset-0 rounded-full bg-kingdom-gold"
                  transition={
                    shouldReduceMotion
                      ? { duration: 0 }
                      : { type: 'spring', stiffness: 420, damping: 34 }
                  }
                />
              )}
              <span className="relative z-10">{label}</span>
            </button>
          )
        })}
      </div>
      <div
        role="tabpanel"
        className={tab === 'chat' ? 'flex min-h-0 flex-1 flex-col' : 'hidden'}
      >
        <ApplyChat {...props} syncedState={syncedState} />
      </div>
      <div
        role="tabpanel"
        className={tab === 'form' ? 'flex min-h-0 flex-1 flex-col' : 'hidden'}
      >
        <ApplyFormStepper {...props} syncedState={syncedState} />
      </div>
    </div>
  )
}
